import * as React from 'react'
import styled from 'styled-components'
import { Spacing, StackChildren, Media } from '@doordash/design-language'
import * as ReactSwipe from 'react-swipe'
import Pagination from '@doordash/component-pagination'
import { Card, CardContent, CardStyleVariants } from './Card'
import * as DA from '../../../typeModules/events'

const Container = styled.div`
  padding: ${Spacing.None}px;
`

const SwipeContainer = styled.div`
  ${Media.Mobile`
    margin: -${Spacing.Small}px 0 -${Spacing.XLarge}px -${Spacing.Small}px;
    width: 100vw;
  `}

  margin-bottom: -${Spacing.XLarge}px;
`

const PaginationContainer = styled.div`
  position: relative;
  bottom: ${Spacing.XxLarge}px;
  left: ${Spacing.Large}px;
  float: left;
`

type PromotionalCardsProps = {
  cards: CardContent[]
  swipeable?: boolean
  cardHeightPx?: number
  styleVariant?: CardStyleVariants
  shouldDisplayIcons?: boolean
  track?: <T>(...args: (DA.EventActions | T | object)[]) => Promise<void>
}

/**
 * UI component for rendering promotional card content.
 * @param {CardContent} cards - data containing card content
 * @param {boolean} [swipeable=false] - renders the cards vertically. Otherwise render in a swipeable banner.
 * @param {cardHeightPx} [cardHeightPx=undefined] - set a fixed height for each card
 * @param {styleVariant} [styleVariant=CardStyleVariants.List] - specify the style for the content in each card
 */
export const PromotionalCards: React.FC<PromotionalCardsProps> & {
  Type?: typeof CardStyleVariants
} = ({
  cards,
  swipeable = false,
  cardHeightPx,
  styleVariant = CardStyleVariants.List,
  shouldDisplayIcons = true,
  track,
}) => {
  const [slideIndex, setSlideIndex] = React.useState<number>(0)

  React.useEffect(() => {
    // Track on first load
    if (track) {
      let title
      let content
      if (cards && cards.length > 0) {
        title = cards[0].title
        content = cards[0].content
      }
      track(
        DA.EventActions.Rendered,
        DA.PromotionsProfileSetupNames.PromotionViewed,
        { title, content }
      )
    }
  }, [track, cards])

  const onSwipe = (i: number) => {
    if (track) {
      let title
      let content
      if (cards && cards.length > 0) {
        title = cards[i].title
        content = cards[i].content
      }
      track(
        DA.EventActions.Rendered,
        DA.PromotionsProfileSetupNames.PromotionViewed,
        { title, content }
      )
    }
  }

  return swipeable ? (
    <SwipeContainer>
      <ReactSwipe
        swipeOptions={{
          continuous: false,
          startSlide: slideIndex,
          transitionEnd: idx => setSlideIndex(idx),
          callback: i => onSwipe(i),
        }}
      >
        {cards.map(card => (
          <Container key={card.title}>
            <StackChildren size={StackChildren.Sizes.Small}>
              <Card
                card={card}
                heightPx={cardHeightPx}
                styleVariant={styleVariant}
              />
            </StackChildren>
            <PaginationContainer>
              <Pagination
                accessibilityLabel={'Pagination'}
                indicatorAccessibilityLabelPrefix={'Pagination'}
                color={Pagination.Colors.Grey}
                shape={Pagination.Shapes.Dot}
                count={cards.length}
                selectedIndex={slideIndex}
                onSelect={idx => {
                  onSwipe(idx)
                  setSlideIndex(idx)
                }}
              />
            </PaginationContainer>
          </Container>
        ))}
      </ReactSwipe>
    </SwipeContainer>
  ) : (
    <Container>
      <StackChildren size={StackChildren.Sizes.Small}>
        {cards.map(card => (
          <Card
            key={card.title}
            card={card}
            heightPx={cardHeightPx}
            styleVariant={styleVariant}
            shouldDisplayIcons={shouldDisplayIcons}
          />
        ))}
      </StackChildren>
    </Container>
  )
}

PromotionalCards.Type = CardStyleVariants
